import { ReactNode } from 'react'
import { LayoutDashboard, FileCheck, FolderLock, QrCode, PenLine, Send, FilePen, MessageCircle, Camera, Image as ImageIcon, Search, UserPlus } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { Header } from './Header'
import { StatusBar } from './StatusBar'
import { useIsSuperAdmin } from '@/shared/hooks/useIsSuperAdmin'

interface DashboardLayoutProps {
  children: ReactNode
  /** @deprecated — el Header maneja la navegación al home. Se mantiene por compat. */
  onNavigateHome?: () => void
}

interface NavItem {
  to: string
  label: string
  icon: ReactNode
  match?: (pathname: string) => boolean
}

const SECTIONS: { label: string; items: NavItem[] }[] = [
  {
    label: 'Inicio',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
    ],
  },
  {
    label: 'Firma',
    items: [
      { to: '/firmar', label: 'Firmar Documento', icon: <FilePen size={20} />, match: p => p === '/firmar' || p === '/firmar-electronica' },
      { to: '/documentos', label: 'Documentos', icon: <Send size={20} />, match: p => p.startsWith('/documentos') },
      { to: '/mis-firmas', label: 'Mis Firmas', icon: <PenLine size={20} /> },
    ],
  },
  {
    label: 'Herramientas',
    items: [
      { to: '/verifier', label: 'Verificar Firmas', icon: <FileCheck size={20} /> },
      { to: '/custody', label: 'Custodia Documental', icon: <FolderLock size={20} /> },
      { to: '/qr-generator', label: 'Generador QR', icon: <QrCode size={20} /> },
      // /blockchain-lookup oculto del nav (la ruta sigue activa para
      // links directos). Sacar de aca cuando se promocione el caso de
      // uso legal (evidencia cripto en juicios).
    ],
  },
]

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const location = useLocation()
  const { isSuperAdmin } = useIsSuperAdmin()
  const sections = isSuperAdmin
    ? [
        ...SECTIONS,
        {
          label: 'Administración',
          items: [{ to: '/invitar', label: 'Invitar usuario', icon: <UserPlus size={20} /> }],
        },
      ]
    : SECTIONS
  const isActive = (item: NavItem) =>
    item.match ? item.match(location.pathname) : location.pathname === item.to

  return (
    <div className="min-h-screen flex flex-col bg-legal-50 dark:bg-legal-900 pb-7">
      <Header />

      <div className="flex-1 flex w-full">
        {/* Sidebar — se extiende a la altura del flex container, no sticky */}
        <aside className="hidden lg:flex flex-col w-72 flex-shrink-0 bg-white dark:bg-legal-800 border-r border-legal-200 dark:border-legal-700">
          <nav className="p-4 space-y-6 sticky top-[73px]">
            {sections.map(section => (
              <div key={section.label}>
                <p className="px-4 text-xs font-semibold text-legal-500 dark:text-legal-400 uppercase tracking-wider mb-2">
                  {section.label}
                </p>
                <div className="space-y-1">
                  {section.items.map(item => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left rounded-lg font-medium transition-colors ${
                        isActive(item)
                          ? 'text-legal-900 dark:text-white bg-legal-100 dark:bg-legal-700'
                          : 'text-legal-600 dark:text-legal-300 hover:bg-legal-50 dark:hover:bg-legal-700/50'
                      }`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}

            {/* Próximamente — items disabled para signaling de roadmap */}
            <div>
              <p className="px-4 text-xs font-semibold text-legal-500 dark:text-legal-400 uppercase tracking-wider mb-2">
                Próximamente
              </p>
              <div className="space-y-1">
                {[
                  { label: 'Blockchain Lookup', icon: <Search size={20} /> },
                  { label: 'Validador WhatsApp', icon: <MessageCircle size={20} /> },
                  { label: 'Captura Web', icon: <Camera size={20} /> },
                  { label: 'Análisis EXIF', icon: <ImageIcon size={20} /> },
                ].map(item => (
                  <button
                    key={item.label}
                    disabled
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-legal-400 dark:text-legal-600 cursor-not-allowed rounded-lg"
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0">
          {children}
        </main>
      </div>

      <StatusBar />
    </div>
  )
}
